import {
  TERMINOLOGY_CONFIGURATION_CODES,
  TERMINOLOGY_USAGE_KINDS,
  TerminologyUsageDeclaration,
  TerminologyUsageSurface,
  defineTerminologyUsage,
} from '@app/shared/terminology/terminology-usage';
import {
  TERMINOLOGY_TOKENS,
  TerminologyTokenCode,
} from '@app/shared/terminology/terminology-token.catalog';

type Token = (typeof TERMINOLOGY_TOKENS)[keyof typeof TERMINOLOGY_TOKENS];

const surface = (
  surfaceCode: string,
  componentCode: string,
): TerminologyUsageSurface => ({
  featureCode: 'phms',
  surfaceCode,
  componentCode,
  locations: [{ kind: 'route', value: '/case-context' }],
});

const sectionHeading = (
  usageId: string,
  usageSurface: TerminologyUsageSurface,
  token: Token,
  fallbackText: string,
): Readonly<{ heading: TerminologyUsageDeclaration }> => ({
  heading: defineTerminologyUsage<TerminologyTokenCode>({
    usageId,
    configurationCode: TERMINOLOGY_CONFIGURATION_CODES.programPack,
    token,
    fallbackText,
    kind: TERMINOLOGY_USAGE_KINDS.componentInput,
    surface: usageSurface,
  }),
});

/** Controlled section-title usages on the case-context Records & History surface. */
export const PHMS_CASE_CONTEXT_TERMINOLOGY = {
  offenses: sectionHeading(
    'phms.case-context.section.offenses',
    surface('case-context-offenses', 'CaseContextOffensePanelComponent'),
    TERMINOLOGY_TOKENS.offenseRecordPlural,
    'Offenses',
  ),
  violations: sectionHeading(
    'phms.case-context.section.violations',
    surface('case-context-violations', 'CaseContextViolationsPanelComponent'),
    TERMINOLOGY_TOKENS.violationRecordPlural,
    'Violations',
  ),
  programming: sectionHeading(
    'phms.case-context.section.programming',
    surface('case-context-programming', 'CaseContextProgrammingPanelComponent'),
    TERMINOLOGY_TOKENS.programmingHistory,
    'Programming',
  ),
  criminalHistory: sectionHeading(
    'phms.case-context.section.criminal-history',
    surface(
      'case-context-criminal-history',
      'CaseContextCriminalHistoryPanelComponent',
    ),
    TERMINOLOGY_TOKENS.criminalHistory,
    'Criminal History',
  ),
  incarcerationHistory: sectionHeading(
    'phms.case-context.section.incarceration-history',
    surface(
      'case-context-incarceration-history',
      'CaseContextIncarcerationHistoryPanelComponent',
    ),
    TERMINOLOGY_TOKENS.incarcerationHistory,
    'Incarceration History',
  ),
  hearingHistory: sectionHeading(
    'phms.case-context.section.hearing-history',
    surface(
      'case-context-hearing-history',
      'CaseContextHearingHistoryPanelComponent',
    ),
    TERMINOLOGY_TOKENS.hearingHistory,
    'Hearing History',
  ),
} as const;
